<template>
  <div>
    <v-btn style="z-index: 100000" class="btn-danger-glow" :small="isMobile" absolute right fab @click="openCancelConfirm()">
      <v-icon :large="!isMobile">mdi-close</v-icon>
    </v-btn>
    <div v-if="!isMobile">
      <v-row no-gutters>
        <v-col cols="auto">
          <v-avatar size="80"> <v-img cover :src="getImageForExercise(exercise)" position="top center"></v-img> </v-avatar>
        </v-col>
        <v-col class="align-center">
          <v-card-title class="pb-0">{{ exercise.title }}</v-card-title>
        </v-col>
      </v-row>
      <v-alert
        v-if="activeTechniqueInfo && activeTechniqueInfo.details"
        style="background: rgba(255, 255, 255, 0.05)"
        :color="activeTechniqueInfo.color"
        border="left"
        elevation="2"
        colored-border
        class="mt-4 mb-4 intensity-alert"
        dark>
        <div class="d-flex align-center">
          <v-icon :color="activeTechniqueInfo.color" class="mr-3">{{ activeTechniqueInfo.icon }}</v-icon>
          <div class="font-weight-bold">{{ activeTechniqueInfo.descriptionShort }}</div>
          <v-btn icon small class="ml-auto" @click="toggleIntensityAlertExpansion">
            <v-icon>{{ isIntensityAlertExpanded ? "mdi-chevron-up" : "mdi-information-outline" }}</v-icon>
          </v-btn>
        </div>
        <v-expand-transition>
          <div v-show="isIntensityAlertExpanded" class="intensity-details-expanded mt-2">
            <v-divider></v-divider>
            <div class="px-1">
              <p class="subtitle-1 font-weight-bold font-italic">"{{ activeTechniqueInfo.details.summary }}"</p>
              <p class="body-2 mb-4">{{ activeTechniqueInfo.description }}</p>
              <v-row>
                <v-col cols="12" md="6" v-if="activeTechniqueInfo.details.advantages && activeTechniqueInfo.details.advantages.length">
                  <h4 class="mt-4 mb-2 text-uppercase text-caption" :style="{ color: getSectionHeadlineColor(activeTechniqueInfo.color) }"></h4>
                  <v-list dense color="transparent" class="pa-0">
                    <v-list-item v-for="(advantage, index) in activeTechniqueInfo.details.advantages" :key="'adv-' + index" class="px-0 py-1">
                      <v-list-item-icon class="mr-3 my-0">
                        <v-icon small :color="getIconColorForItem('advantage')">{{ advantage.icon }}</v-icon>
                      </v-list-item-icon>

                      <v-list-item-content class="py-0">
                        <v-list-item-title class="body-2 white--text">{{ advantage.text }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>

                <v-col cols="12" md="6" v-if="activeTechniqueInfo.details.whyDoIt && activeTechniqueInfo.details.whyDoIt.length">
                  <h4 class="mt-4 mb-2 text-uppercase text-caption" :style="{ color: getSectionHeadlineColor(activeTechniqueInfo.color, true) }">
                    Warum diese Technik?
                  </h4>

                  <v-list dense color="transparent" class="pa-0">
                    <v-list-item v-for="(reason, index) in activeTechniqueInfo.details.whyDoIt" :key="'reason-' + index" class="px-0 py-1">
                      <v-list-item-icon class="mr-3 my-0">
                        <v-icon small :color="getIconColorForItem('reason')">{{ reason.icon }}</v-icon>
                      </v-list-item-icon>

                      <v-list-item-content class="py-0">
                        <v-list-item-title class="body-2 white--text">{{ reason.text }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-expand-transition>
      </v-alert>

      <v-row no-gutters>
        <v-col cols="12">
          <v-divider color="white"></v-divider>
        </v-col>

        <v-col cols="12" v-if="exercise.general_exercise_settings && exercise.general_exercise_settings.length">
          <v-row no-gutters>
            <v-col cols="auto">
              <v-card-title class="px-1 py-1 font-weight-bold">Geräteeinstellungen</v-card-title>
            </v-col>

            <v-col class="align-content-center" v-if="savedGeneralExerciseSettings" cols="grow">
              <v-btn class="ma-1" x-small fab color="blue" @click="savedGeneralExerciseSettings = false">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>

              <v-chip
                color="grey"
                class="mr-2"
                v-for="(setting, index) in exercise.general_exercise_settings.filter((setting) => setting.value)"
                :key="'setting' + index">
                <span>{{ shorten(setting.text) + " | " + setting.value }}</span>
              </v-chip>
            </v-col>
          </v-row>

          <v-expand-transition mode="out-in" v-if="!savedGeneralExerciseSettings">
            <v-row no-gutters>
              <v-col
                cols="auto"
                class="pa-1 d-flex align-center"
                :key="index + 'deviceSetting'"
                v-for="(deviceSetting, index) in exercise.general_exercise_settings">
                <v-card
                  class="input-card"
                  :style="deviceSetting && deviceSetting.input === 'selection' ? 'width: 150px !important' : 'width: 150px !important'"
                  flat
                  color="rgba(255,255,255,0.08)">
                  <div class="text-caption grey--text text--lighten-1 px-2 pt-1">{{ shorten(deviceSetting.text, 10) }}</div>

                  <v-select
                    dense
                    solo
                    flat
                    style="line-height: 1"
                    hide-details
                    class="centered-input"
                    v-model="deviceSetting.value"
                    :items="deviceSetting.selections"
                    v-if="deviceSetting && deviceSetting.input === 'selection'"
                    @keydown.enter.prevent="saveGeneralExerciseSettings()"></v-select>

                  <v-text-field
                    v-else
                    dense
                    solo
                    flat
                    hide-details
                    class="centered-input"
                    @input="updatingSetting($event, deviceSetting)"
                    :type="deviceSetting.input"
                    :inputmode="deviceSetting.input === 'number' ? 'numeric' : 'text'"
                    v-model="deviceSetting.value"></v-text-field>
                </v-card>
              </v-col>

              <v-col cols="auto" class="ml-2 align-content-center">
                <v-btn x-small fab color="primary" @click="saveGeneralExerciseSettings()">
                  <v-icon>mdi-check</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-expand-transition>

          <v-divider color="white"></v-divider>
        </v-col>

        <template v-if="!isSensoProExercise">
          <v-col cols="12" class="mt-4">
            <div class="sets-display-desktop">
              <div class="sets-list-wrapper">
                <p class="text-h6 font-weight-bold">Sätze</p>
                <div v-if="combinedExerciseSets.length > 0" class="sets-list">
                  <div v-for="(set, index) in combinedExerciseSets" :key="index" class="set-item" @click="editSet(index)">
                    <v-icon class="set-icon" :color="exercise.warmup && index === 0 ? 'orange' : 'primary'">
                      {{ exercise.warmup && index === 0 ? "mdi-fire" : "mdi-weight-lifter" }}
                    </v-icon>
                    <div class="set-details">
                      <span class="font-weight-bold">{{
                        exercise.warmup && index === 0 ? "Aufwärmsatz" : "Satz " + (exercise.warmup ? index : index + 1)
                      }}</span>
                      <span class="grey--text text--lighten-1">{{ formatSetForChip(set) }}</span>
                    </div>
                    <v-icon v-if="!exercise.method && selectedTechniqueMode !== 'sst7'" class="edit-icon">mdi-pencil-outline</v-icon>
                  </div>
                </div>
                <p v-else class="grey--text mt-4">Noch keine Sätze hinzugefügt.</p>
              </div>

              <div v-if="!exercise.method" class="input-form-wrapper glass-card pa-4 mr-2">
                <p class="text-h6 font-weight-bold mb-4">{{ editingSetIndex !== null ? "Satz bearbeiten" : "Satz hinzufügen" }}</p>
                <template v-if="!exercise.sets || !exercise.sets.length">
                  <v-item-group v-model="selectedTechniqueMode" mandatory class="technique-group mb-4">
                    <v-item v-for="mode in availableTechniqueModes" :key="mode.value" :value="mode.value" v-slot="{ active, toggle }">
                      <div @click="toggle" class="technique-item" :class="{ active: active }">
                        <v-icon class="icon">{{ mode.icon }}</v-icon>
                        <span class="label">{{ mode.text }}</span>
                      </div>
                    </v-item>
                  </v-item-group>
                </template>
                <template v-if="selectedTechniqueMode === 'normal' || selectedTechniqueMode === 'warmup'">
                  <v-col cols="12" class="px-0">
                    <v-row no-gutters>
                      <v-col
                        cols="auto"
                        class="pa-1 d-flex align-center fill-height"
                        :key="index + 'setSetting'"
                        v-for="(setSetting, index) in orderedSetSettings">
                        <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                          <div class="text-caption grey--text text--lighten-1 px-2 pt-1">{{ shorten(setSetting.text, 10) }}</div>

                          <v-select
                            dense
                            solo
                            height="20px"
                            style="line-height: 1"
                            hide-details
                            class="pa-0 ma-0"
                            v-model="setSetting.value"
                            :items="setSetting.selections"
                            v-if="setSetting && setSetting.input === 'selection'"
                            @keydown.enter.prevent="saveSet()">
                          </v-select>

                          <v-text-field
                            v-else
                            v-model="setSetting.value"
                            :placeholder="setSetting.text === 'Notiz' ? 'Optional' : '0'"
                            :type="setSetting.text === 'Pause' ? 'number' : setSetting.input"
                            :inputmode="setSetting.text === 'Pause' || setSetting.input === 'number' ? 'numeric' : 'text'"
                            class="centered-input"
                            solo
                            dense
                            flat
                            :suffix="setSetting.text === 'Pause' ? 'sek' : setSetting.text === 'Gewicht' ? 'kg' : ''"
                            hide-details
                            @keydown.enter.prevent="saveSet()"></v-text-field>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col cols="10" class="justify-content-center text-center mt-3">
                    <v-btn class="btn-primary-glow" @click="saveSet()"> {{ editingSetIndex === null ? "Hinzufügen" : "Speichern" }} </v-btn>
                    <v-btn v-if="editingSetIndex !== null" class="btn-secondary ml-2" @click="cancelEditSet"> Abbrechen </v-btn>
                    <v-btn v-if="editingSetIndex !== null" class="btn-danger-glow ml-2" @click="removeSetByIndex(editingSetIndex)">Löschen </v-btn>
                  </v-col>
                </template>
                <template v-if="selectedTechniqueMode !== 'normal' && selectedTechniqueMode !== 'warmup'">
                  <v-col class="mt-2 px-0" cols="12">
                    <v-row no-gutters>
                      <template v-if="selectedTechniqueMode === 'sst7'">
                        <div v-if="sstWizardStep === 1">
                          <p class="font-weight-bold">Schritt 1: Sätze 1-4</p>
                          <p class="caption">
                            Gib dein Maximalgewicht für 12 saubere Wiederholungen ein. Die ersten 4 Sätze werden automatisch damit generiert.
                          </p>
                          <v-col cols="auto" class="pa-1">
                            <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                              <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Gewicht</div>
                              <v-text-field
                                v-model="techniqueSst7.weightStep1"
                                type="number"
                                suffix="kg"
                                dense
                                solo
                                flat
                                hide-details
                                class="centered-input"></v-text-field>
                            </v-card>
                          </v-col>

                          <v-btn color="primary" class="mt-4" @click="generateSstSets(1)" :disabled="!techniqueSst7.weightStep1 > 0"
                            >Sätze 1-4 erstellen</v-btn
                          >
                        </div>
                        <div v-if="sstWizardStep === 2">
                          <p class="font-weight-bold">Schritt 2: Satz 5 (Langsam hin, schnell zurück)</p>
                          <p class="caption">Gewicht für 6-9 Wiederholungen eingeben.</p>
                          <v-col cols="auto" class="pa-1">
                            <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                              <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Gewicht Satz 5</div>
                              <v-text-field
                                v-model="techniqueSst7.weightStep2"
                                type="number"
                                suffix="kg"
                                dense
                                solo
                                flat
                                hide-details
                                class="centered-input"></v-text-field>
                            </v-card>
                          </v-col>
                          <v-btn color="primary" class="mt-4" @click="generateSstSets(2)" :disabled="!techniqueSst7.weightStep2 > 0"
                            >Satz 5 erstellen</v-btn
                          >
                        </div>
                        <div v-if="sstWizardStep === 3">
                          <p class="font-weight-bold">Schritt 3: Satz 6 (Schnell hin, langsam zurück)</p>
                          <p class="caption">Gewicht für 6-9 Wiederholungen eingeben.</p>
                          <v-col cols="auto" class="pa-1">
                            <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                              <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Gewicht Satz 6</div>
                              <v-text-field
                                v-model="techniqueSst7.weightStep3"
                                type="number"
                                suffix="kg"
                                dense
                                solo
                                flat
                                hide-details
                                class="centered-input"></v-text-field>
                            </v-card>
                          </v-col>
                          <v-btn color="primary" class="mt-4" @click="generateSstSets(3)" :disabled="!techniqueSst7.weightStep3 > 0"
                            >Satz 6 erstellen</v-btn
                          >
                        </div>
                        <div v-if="sstWizardStep === 4">
                          <p class="font-weight-bold">Schritt 4: Satz 7 (Statisch halten)</p>
                          <p class="caption">Gewicht für 30 Sekunden statisches Halten eingeben.</p>
                          <v-col cols="auto" class="pa-1">
                            <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                              <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Gewicht Satz 7</div>
                              <v-text-field
                                v-model="techniqueSst7.weightStep4"
                                type="number"
                                suffix="kg"
                                dense
                                solo
                                flat
                                hide-details
                                class="centered-input"></v-text-field>
                            </v-card>
                          </v-col>
                          <v-btn color="primary" class="mt-4" @click="generateSstSets(4)" :disabled="!techniqueSst7.weightStep4 > 0"
                            >Satz 7 erstellen & Technik abschliessen</v-btn
                          >
                        </div>
                      </template>
                      <template v-if="selectedTechniqueMode === 'dropset'">
                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Max-Gewicht</div>

                            <v-text-field
                              min="0"
                              v-model="techniqueDropset.maxWeight"
                              type="number"
                              suffix="kg"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>

                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Sätze</div>

                            <v-text-field
                              v-model="techniqueDropset.setCount"
                              type="number"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>

                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Min-WDH</div>

                            <v-text-field
                              v-model="techniqueDropset.minReps"
                              type="number"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>

                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Max-WDH</div>

                            <v-text-field
                              v-model="techniqueDropset.maxReps"
                              type="number"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>

                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Reduktion</div>

                            <v-text-field
                              v-model="techniqueDropset.reductionWeight"
                              type="number"
                              suffix="kg"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>
                      </template>

                      <template v-if="selectedTechniqueMode === 'reversedropset'">
                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Max-Gewicht</div>

                            <v-text-field
                              v-model="techniqueReverseDropset.maxWeight"
                              type="number"
                              suffix="kg"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>

                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Sätze</div>

                            <v-text-field
                              v-model="techniqueReverseDropset.setCount"
                              type="number"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>

                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Min-WDH</div>

                            <v-text-field
                              v-model="techniqueReverseDropset.minReps"
                              type="number"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>

                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Max-WDH</div>

                            <v-text-field
                              v-model="techniqueReverseDropset.maxReps"
                              type="number"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>

                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Erhöhung</div>

                            <v-text-field
                              v-model="techniqueReverseDropset.expandWeight"
                              type="number"
                              suffix="kg"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>
                      </template>

                      <template v-if="selectedTechniqueMode === 'schockwelle'">
                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Startgewicht</div>

                            <v-text-field
                              v-model="techniqueSchockwelle.startWeight"
                              type="number"
                              suffix="kg"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>

                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Zuwachs</div>

                            <v-text-field
                              v-model="techniqueSchockwelle.incrementWeight"
                              type="number"
                              suffix="kg"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>

                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Pause</div>

                            <v-text-field
                              v-model="techniqueSchockwelle.pause"
                              type="number"
                              suffix="sek"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>
                      </template>

                      <template v-if="selectedTechniqueMode === 'fst7'">
                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Gewicht</div>

                            <v-text-field
                              v-model="techniqueFst7.weight"
                              type="number"
                              suffix="kg"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>

                        <v-col cols="auto" class="pa-1">
                          <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                            <div class="text-caption grey--text text--lighten-1 px-2 pt-1">Pause</div>

                            <v-text-field
                              v-model="techniqueFst7.pause"
                              type="number"
                              suffix="sek"
                              dense
                              solo
                              flat
                              hide-details
                              class="centered-input"></v-text-field>
                          </v-card>
                        </v-col>
                      </template>
                    </v-row>
                  </v-col>

                  <v-col
                    cols="10"
                    class="justify-content-center text-center mt-3"
                    v-if="selectedTechniqueMode !== 'normal' && selectedTechniqueMode !== 'warmup' && (!exercise.sets || !exercise.sets.length)">
                    <v-btn class="btn-primary-glow" @click="generateSetsForTechnique"> Sätze generieren </v-btn>
                  </v-col>
                </template>
                {{ exercise.method }}
              </div>
              <div v-else>
                <v-fade-transition appear>
                  <div class="confirmation-card-wow text-center" :style="{ '--glow-color': '#4CAF50' }">
                    <div class="icon-wrapper">
                      <v-icon size="60" :color="activeTechniqueInfo.color || 'green'">mdi-check-circle-outline</v-icon>
                    </div>

                    <h3 class="text-h6 font-weight-bold mt-4">Technik aktiv!</h3>
                    <p class="body-1 mt-2">
                      Die Sätze für <b>{{ activeTechniqueInfo.descriptionShort }}</b> wurden generiert.
                    </p>

                    <v-btn rounded color="red" outlined @click="resetTechnique()" class="mt-4 reset-button">
                      <v-icon left>mdi-refresh</v-icon>
                      Zurücksetzen
                    </v-btn>
                  </div>
                </v-fade-transition>
              </div>
            </div>
          </v-col>
        </template>

        <v-col cols="12" v-if="!isButtonDisabled" class="mt-4">
          <v-card-actions class="justify-content-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <div v-on="on" style="display: inline-block">
                  <v-btn class="btn-primary-glow" large :disabled="isButtonDisabled" @click="onButtonClick">Änderungen speichern</v-btn>
                </div>
              </template>

              <span>{{ tooltipMessage }}</span>
            </v-tooltip>
          </v-card-actions>
        </v-col>
      </v-row>
    </div>
    <div v-else class="mobile-wizard-container">
      <v-app-bar flat color="transparent" absolute>
        <v-spacer></v-spacer>
        <v-btn icon @click="openCancelConfirm()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-app-bar>

      <div class="my-2"><v-img src="/img/roody_logo.png" height="65px" contain /></div>

      <v-progress-linear :value="(mobileStepper / 5) * 100" color="primary" height="6" class="mobile-wizard-progress"></v-progress-linear>

      <div class="mobile-wizard-content">
        <v-fade-transition mode="out-in">
          <div v-if="mobileStepper === 1" class="mobile-wizard-step" key="step1">
            <h1 class="text-h4 font-weight-bold mb-2">Geräteeinstellungen</h1>
            <p class="body-1 grey--text text--lighten-1 mb-8">Optional, für ein schnelles Setup im Training.</p>

            <div v-if="exercise.general_exercise_settings.length > 0">
              <v-row :justify="exercise.general_exercise_settings.length < 3 ? 'center' : 'start'">
                <v-col
                  :cols="exercise.general_exercise_settings.length < 3 ? '10' : '6'"
                  v-for="(setting, i) in exercise.general_exercise_settings"
                  :key="i"
                  class="pa-1">
                  <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                    <div class="text-caption grey--text text--lighten-1 px-2 pt-1">{{ shorten(setting.text, 15) }}</div>
                    <v-text-field
                      v-model="setting.value"
                      :type="setting.input === 'number' ? 'number' : 'text'"
                      :inputmode="setting.input === 'number' ? 'numeric' : 'text'"
                      dense
                      solo
                      flat
                      hide-details
                      class="centered-input" />
                  </v-card>
                </v-col>
              </v-row>
            </div>
            <div v-else class="text-center grey--text py-12">
              <v-icon large class="mb-2">mdi-cogs</v-icon>
              <p>Für diese Übung sind keine<br />spezifischen Geräteeinstellungen nötig.</p>
            </div>
          </div>

          <div v-else-if="mobileStepper === 2" class="mobile-wizard-step" key="step2">
            <h1 class="text-h4 font-weight-bold mb-2">Aufwärmsatz</h1>
            <p class="body-1 grey--text text--lighten-1 mb-8">Möchtest du dich vor den Arbeitssätzen aufwärmen?</p>
            <v-expand-transition>
              <div v-if="hasWarmup === null" class="d-flex flex-column" key="warmup-choice">
                <v-card outlined class="choice-card mb-4" @click="addWarmupSet()">
                  <v-card-text class="d-flex align-center">
                    <v-icon color="orange" large left>mdi-fire</v-icon>
                    <span class="text-h6">Ja, hinzufügen</span>
                    <v-icon class="ml-auto">mdi-chevron-right</v-icon>
                  </v-card-text>
                </v-card>
                <v-card
                  outlined
                  class="choice-card"
                  @click="
                    hasWarmup = null
                    mobileStepper++
                  ">
                  <v-card-text class="d-flex align-center">
                    <v-icon large left>mdi-skip-next</v-icon>
                    <span class="text-h6">Nein, überspringen</span>
                    <v-icon class="ml-auto">mdi-chevron-right</v-icon>
                  </v-card-text>
                </v-card>
              </div>
              <div v-else-if="hasWarmup === true" key="warmup-inputs">
                <p class="caption">Lege die Werte für deinen einmaligen Aufwärmsatz fest.</p>
                <v-row no-gutters dense>
                  <v-col cols="6" v-for="(setting, i) in exercise.warmup" :key="i" class="pa-1">
                    <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                      <div class="text-caption grey--text text--lighten-1 px-2 pt-1">{{ setting.text }}</div>
                      <v-text-field
                        v-model="setting.value"
                        :type="setting.input === 'number' ? 'number' : 'text'"
                        :inputmode="setting.input === 'number' ? 'numeric' : 'text'"
                        :suffix="setting.text === 'Pause' ? 's' : setting.text === 'Gewicht' ? 'kg' : ''"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input" />
                    </v-card>
                  </v-col>
                  <v-col cols="12" class="text-center">
                    <v-btn text color="grey" @click="hasWarmup = null">abbrechen</v-btn>
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>
          </div>

          <div v-else-if="mobileStepper === 3" class="mobile-wizard-step" key="step3">
            <h1 class="text-h4 font-weight-bold mb-2">Trainingsart</h1>
            <p class="body-1 grey--text text--lighten-1 mb-8">Wähle normale Sätze oder eine spezielle Intensitätstechnik.</p>
            <v-item-group v-model="selectedTechniqueMode" mandatory>
              <v-row dense>
                <v-col cols="12" v-for="mode in mobileTechniqueModes" :key="mode.value">
                  <v-item :value="mode.value" v-slot="{ active, toggle }">
                    <v-card
                      :class="['technique-tile', { 'technique-tile--active': active }]"
                      @click="toggle"
                      :style="{ '--tech-color': intensityTechniqueMapping[mode.value].color }">
                      <div class="d-flex align-center pa-4">
                        <v-icon class="mr-4" large :color="active ? 'white' : intensityTechniqueMapping[mode.value].color">{{ mode.icon }}</v-icon>
                        <div class="flex-grow-1">
                          <div class="font-weight-bold">{{ intensityTechniqueMapping[mode.value].descriptionShort }}</div>
                          <p class="caption mb-0 text--lighten-2">{{ intensityTechniqueMapping[mode.value].details.summary }}</p>
                        </div>
                        <v-btn icon @click.stop="toggleIntensityAlertExpansionMobile(mode.value)">
                          <v-icon>{{ isIntensityAlertExpandedMobile === mode.value ? "mdi-chevron-up" : "mdi-information-outline" }}</v-icon>
                        </v-btn>
                      </div>
                      <v-expand-transition>
                        <div v-show="isIntensityAlertExpandedMobile === mode.value">
                          <v-divider class="mx-4"></v-divider>
                          <div class="px-4 py-3">
                            <p class="body-2 mb-4">{{ intensityTechniqueMapping[mode.value].description }}</p>
                            <div
                              v-if="
                                intensityTechniqueMapping[mode.value].details.advantages &&
                                intensityTechniqueMapping[mode.value].details.advantages.length
                              ">
                              <h4
                                class="mb-2 text-uppercase text-caption"
                                :style="{ color: getSectionHeadlineColor(intensityTechniqueMapping[mode.value].color) }">
                                Vorteile
                              </h4>
                              <v-list dense color="transparent" class="pa-0">
                                <v-list-item
                                  v-for="(advantage, index) in intensityTechniqueMapping[mode.value].details.advantages"
                                  :key="'adv-' + index"
                                  class="px-0 py-1">
                                  <v-list-item-icon class="mr-3 my-0"
                                    ><v-icon small :color="getIconColorForItem('advantage')">{{ advantage.icon }}</v-icon></v-list-item-icon
                                  >
                                  <v-list-item-content class="py-0"
                                    ><v-list-item-title class="body-2 white--text">{{ advantage.text }}</v-list-item-title></v-list-item-content
                                  >
                                </v-list-item>
                              </v-list>
                            </div>
                            <div
                              v-if="
                                intensityTechniqueMapping[mode.value].details.whyDoIt && intensityTechniqueMapping[mode.value].details.whyDoIt.length
                              ">
                              <h4
                                class="mt-4 mb-2 text-uppercase text-caption"
                                :style="{ color: getSectionHeadlineColor(intensityTechniqueMapping[mode.value].color, true) }">
                                Warum diese Technik?
                              </h4>
                              <v-list dense color="transparent" class="pa-0">
                                <v-list-item
                                  v-for="(reason, index) in intensityTechniqueMapping[mode.value].details.whyDoIt"
                                  :key="'reason-' + index"
                                  class="px-0 py-1">
                                  <v-list-item-icon class="mr-3 my-0"
                                    ><v-icon small :color="getIconColorForItem('reason')">{{ reason.icon }}</v-icon></v-list-item-icon
                                  >
                                  <v-list-item-content class="py-0"
                                    ><v-list-item-title class="body-2 white--text">{{ reason.text }}</v-list-item-title></v-list-item-content
                                  >
                                </v-list-item>
                              </v-list>
                            </div>
                          </div>
                        </div>
                      </v-expand-transition>
                    </v-card>
                  </v-item>
                </v-col>
              </v-row>
            </v-item-group>
          </div>

          <div v-else-if="mobileStepper === 4" class="mobile-wizard-step" key="step4">
            <div v-if="selectedTechniqueMode === 'normal' || !exercise.method">
              <h1 class="text-h4 font-weight-bold mb-2">Sätze definieren</h1>
              <p class="body-1 grey--text text--lighten-1 mb-6">Gib die Werte für dein Training an.</p>

              <transition-group name="set-list" tag="div" class="set-list-container mb-4">
                <v-card
                  v-for="(set, index) in combinedExerciseSets"
                  :key="getSetKey(index, set)"
                  class="set-list-item"
                  :style="{ '--border-color': getSetIcon(index).color }">
                  <v-icon class="mr-3" :color="getSetIcon(index).color">{{ getSetIcon(index).icon }}</v-icon>
                  <div class="d-flex flex-column flex-grow-1">
                    <span class="font-weight-bold">{{ getSetTitle(index) }}</span>
                    <span class="caption">{{ formatSetForChip(set) }}</span>
                  </div>
                  <v-btn icon small @click="removeSetByIndex(exercise.warmup && index === 0 ? -1 : index - (exercise.warmup ? 1 : 0))">
                    <v-icon small>mdi-close-circle</v-icon>
                  </v-btn>
                </v-card>
              </transition-group>

              <div v-if="selectedTechniqueMode === 'normal'">
                <v-row dense>
                  <v-col cols="6" class="pa-1" v-for="(setting, i) in orderedSetSettings" :key="i">
                    <v-card class="input-card" flat color="rgba(255,255,255,0.08)">
                      <div class="text-caption grey--text text--lighten-1 px-2 pt-1">{{ setting.text }}</div>
                      <v-select
                        v-if="setting.input === 'selection'"
                        v-model="setting.value"
                        :items="setting.selections"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input"></v-select>
                      <v-text-field
                        v-else
                        v-model="setting.value"
                        :type="setting.input"
                        :inputmode="setting.input === 'number' ? 'numeric' : 'text'"
                        :suffix="setting.text === 'Pause' ? 's' : setting.text === 'Gewicht' ? 'kg' : ''"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input"></v-text-field>
                    </v-card>
                  </v-col>
                </v-row>
                <v-btn depressed block x-large color="primary" @click="saveSet" class="mt-4"><v-icon left>mdi-plus</v-icon>Satz hinzufügen</v-btn>
              </div>
              <div v-else>
                <v-row dense v-if="selectedTechniqueMode === 'dropset'">
                  <v-col cols="6"
                    ><v-card class="input-card" flat color="rgba(255,255,255,0.08)"
                      ><div class="text-caption grey--text text--lighten-1 px-2 pt-1">Max-Gewicht</div>
                      <v-text-field
                        v-model="techniqueDropset.maxWeight"
                        type="number"
                        suffix="kg"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input"></v-text-field></v-card
                  ></v-col>
                  <v-col cols="6"
                    ><v-card class="input-card" flat color="rgba(255,255,255,0.08)"
                      ><div class="text-caption grey--text text--lighten-1 px-2 pt-1">Sätze</div>
                      <v-text-field
                        v-model="techniqueDropset.setCount"
                        type="number"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input"></v-text-field></v-card
                  ></v-col>
                  <v-col cols="6"
                    ><v-card class="input-card" flat color="rgba(255,255,255,0.08)"
                      ><div class="text-caption grey--text text--lighten-1 px-2 pt-1">Reduktion</div>
                      <v-text-field
                        v-model="techniqueDropset.reductionWeight"
                        type="number"
                        suffix="kg"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input"></v-text-field></v-card
                  ></v-col>
                </v-row>
                <v-row dense v-if="selectedTechniqueMode === 'reversedropset'">
                  <v-col cols="6"
                    ><v-card class="input-card" flat color="rgba(255,255,255,0.08)"
                      ><div class="text-caption grey--text text--lighten-1 px-2 pt-1">Max-Gewicht</div>
                      <v-text-field
                        v-model="techniqueReverseDropset.maxWeight"
                        type="number"
                        suffix="kg"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input"></v-text-field></v-card
                  ></v-col>
                  <v-col cols="6"
                    ><v-card class="input-card" flat color="rgba(255,255,255,0.08)"
                      ><div class="text-caption grey--text text--lighten-1 px-2 pt-1">Sätze</div>
                      <v-text-field
                        v-model="techniqueReverseDropset.setCount"
                        type="number"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input"></v-text-field></v-card
                  ></v-col>
                  <v-col cols="6"
                    ><v-card class="input-card" flat color="rgba(255,255,255,0.08)"
                      ><div class="text-caption grey--text text--lighten-1 px-2 pt-1">Erhöhung</div>
                      <v-text-field
                        v-model="techniqueReverseDropset.expandWeight"
                        type="number"
                        suffix="kg"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input"></v-text-field></v-card
                  ></v-col>
                </v-row>
                <v-row dense v-if="selectedTechniqueMode === 'schockwelle'">
                  <v-col cols="6"
                    ><v-card class="input-card" flat color="rgba(255,255,255,0.08)"
                      ><div class="text-caption grey--text text--lighten-1 px-2 pt-1">Startgewicht</div>
                      <v-text-field
                        v-model="techniqueSchockwelle.startWeight"
                        type="number"
                        suffix="kg"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input"></v-text-field></v-card
                  ></v-col>
                  <v-col cols="6"
                    ><v-card class="input-card" flat color="rgba(255,255,255,0.08)"
                      ><div class="text-caption grey--text text--lighten-1 px-2 pt-1">Zuwachs</div>
                      <v-text-field
                        v-model="techniqueSchockwelle.incrementWeight"
                        type="number"
                        suffix="kg"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input"></v-text-field></v-card
                  ></v-col>
                  <v-col cols="6"
                    ><v-card class="input-card" flat color="rgba(255,255,255,0.08)"
                      ><div class="text-caption grey--text text--lighten-1 px-2 pt-1">Pause</div>
                      <v-text-field
                        v-model="techniqueSchockwelle.pause"
                        type="number"
                        suffix="s"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input"></v-text-field></v-card
                  ></v-col>
                </v-row>
                <v-row dense v-if="selectedTechniqueMode === 'fst7'">
                  <v-col cols="6"
                    ><v-card class="input-card" flat color="rgba(255,255,255,0.08)"
                      ><div class="text-caption grey--text text--lighten-1 px-2 pt-1">Gewicht</div>
                      <v-text-field
                        v-model="techniqueFst7.weight"
                        type="number"
                        suffix="kg"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input"></v-text-field></v-card
                  ></v-col>
                  <v-col cols="6"
                    ><v-card class="input-card" flat color="rgba(255,255,255,0.08)"
                      ><div class="text-caption grey--text text--lighten-1 px-2 pt-1">Pause</div>
                      <v-text-field
                        v-model="techniqueFst7.pause"
                        type="number"
                        suffix="s"
                        dense
                        solo
                        flat
                        hide-details
                        class="centered-input"></v-text-field></v-card
                  ></v-col>
                </v-row>
                <div v-if="selectedTechniqueMode === 'sst7' && !exercise.sets.length">
                  <p class="caption">
                    Der SST-7 Wizard ist für die Desktop-Ansicht optimiert. Hier kannst du die Startwerte eingeben, um die 7 Sätze zu generieren.
                  </p>
                  <v-row dense>
                    <v-col cols="6"
                      ><v-card class="input-card" flat color="rgba(255,255,255,0.08)"
                        ><div class="text-caption grey--text text--lighten-1 px-2 pt-1">Startgewicht (12RM)</div>
                        <v-text-field
                          v-model="techniqueSst7.startWeight"
                          type="number"
                          suffix="kg"
                          dense
                          solo
                          flat
                          hide-details
                          class="centered-input"></v-text-field></v-card
                    ></v-col>
                    <v-col cols="6"
                      ><v-card class="input-card" flat color="rgba(255,255,255,0.08)"
                        ><div class="text-caption grey--text text--lighten-1 px-2 pt-1">Reduktion</div>
                        <v-text-field
                          v-model="techniqueSst7.reductionWeight"
                          type="number"
                          suffix="kg"
                          dense
                          solo
                          flat
                          hide-details
                          class="centered-input"></v-text-field></v-card
                    ></v-col>
                  </v-row>
                </div>

                <v-btn v-if="!exercise.sets.length" class="btn-primary-glow mt-6" x-large block @click="generateSetsForTechnique">
                  <v-icon left>mdi-auto-fix</v-icon> Sätze generieren
                </v-btn>
                <v-btn v-else text block color="red" @click="resetTechnique()" class="mt-4">
                  <v-icon left>mdi-refresh</v-icon> Sätze zurücksetzen
                </v-btn>
              </div>
            </div>

            <div v-else>
              <div class="confirmation-card-wow text-center" :style="{ '--glow-color': activeTechniqueInfo.color || '#4CAF50' }">
                <div class="icon-wrapper">
                  <v-icon size="60" :color="activeTechniqueInfo.color || 'green'">mdi-check-circle-outline</v-icon>
                </div>
                <h3 class="text-h6 font-weight-bold mt-4">Technik aktiv!</h3>
                <p class="body-1 mt-2">
                  Die Sätze für <b>{{ activeTechniqueInfo.descriptionShort }}</b> wurden generiert.
                </p>
                <div class="generated-sets-list">
                  <v-list dense color="transparent">
                    <v-list-item v-for="(set, index) in combinedExerciseSets" :key="index">
                      <v-list-item-icon
                        ><v-icon :color="getSetIcon(index).color">{{ getSetIcon(index).icon }}</v-icon></v-list-item-icon
                      >
                      <v-list-item-content
                        ><v-list-item-title>{{ getSetTitle(index) }}</v-list-item-title></v-list-item-content
                      >
                      <v-list-item-action
                        ><span class="font-weight-bold">{{ formatSetForChip(set) }}</span></v-list-item-action
                      >
                    </v-list-item>
                  </v-list>
                </div>
                <v-btn rounded color="red" outlined @click="resetTechnique()" class="mt-4 reset-button">
                  <v-icon left>mdi-refresh</v-icon>
                  Zurücksetzen
                </v-btn>
              </div>
            </div>
          </div>

          <div v-else-if="mobileStepper === 5" class="mobile-wizard-step" key="step5">
            <h1 class="text-h4 font-weight-bold mb-2">Zusammenfassung</h1>
            <p class="body-1 grey--text text--lighten-1 mb-8">Überprüfe deine Konfiguration vor dem Speichern.</p>

            <v-card flat color="rgba(255,255,255,0.05)" class="mb-4 pa-2">
              <v-list-item v-if="exercise.general_exercise_settings.some((s) => s.value)">
                <v-list-item-content>
                  <v-list-item-subtitle>Geräteeinstellungen</v-list-item-subtitle>
                  <v-list-item-title class="d-flex flex-wrap">
                    <v-chip small class="ma-1" v-for="setting in exercise.general_exercise_settings.filter((s) => s.value)" :key="setting.text">
                      {{ shorten(setting.text) }}: {{ setting.value }}
                    </v-chip>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider v-if="exercise.general_exercise_settings.some((s) => s.value) && exercise.warmup"></v-divider>
              <v-list-item v-if="exercise.warmup">
                <v-list-item-icon><v-icon color="orange">mdi-fire</v-icon></v-list-item-icon>
                <v-list-item-content
                  ><v-list-item-subtitle>Aufwärmsatz</v-list-item-subtitle
                  ><v-list-item-title>{{ formatSetForChip(exercise.warmup) }}</v-list-item-title></v-list-item-content
                >
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item v-if="activeTechniqueInfo">
                <v-list-item-icon
                  ><v-icon :color="activeTechniqueInfo.color">{{ activeTechniqueInfo.icon }}</v-icon></v-list-item-icon
                >
                <v-list-item-content
                  ><v-list-item-subtitle>Trainingsart</v-list-item-subtitle
                  ><v-list-item-title>{{ activeTechniqueInfo.descriptionShort }}</v-list-item-title></v-list-item-content
                >
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item>
                <v-list-item-icon><v-icon>mdi-format-list-numbered</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Arbeitssätze</v-list-item-subtitle>
                  <v-list-item-title>{{ exercise.sets.length }} Sätze</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-icon><v-icon>mdi-weight-kilogram</v-icon></v-list-item-icon>
                <v-list-item-content
                  ><v-list-item-subtitle>Gewicht</v-list-item-subtitle
                  ><v-list-item-title>{{ exerciseSummary.weightRange }}</v-list-item-title></v-list-item-content
                >
              </v-list-item>
              <v-list-item>
                <v-list-item-icon><v-icon>mdi-repeat</v-icon></v-list-item-icon>
                <v-list-item-content
                  ><v-list-item-subtitle>Wiederholungen</v-list-item-subtitle
                  ><v-list-item-title>{{ exerciseSummary.repRange }}</v-list-item-title></v-list-item-content
                >
              </v-list-item>
              <v-list-item v-if="exerciseSummary.durationRange !== 'N/A'">
                <v-list-item-icon><v-icon>mdi-clock-outline</v-icon></v-list-item-icon>
                <v-list-item-content
                  ><v-list-item-subtitle>Dauer</v-list-item-subtitle
                  ><v-list-item-title>{{ exerciseSummary.durationRange }}</v-list-item-title></v-list-item-content
                >
              </v-list-item>
            </v-card>
          </div>
        </v-fade-transition>
      </div>

      <div class="mobile-wizard-nav">
        <v-btn text @click="mobileStepper--" v-if="mobileStepper > 1">
          <v-icon left>mdi-chevron-left</v-icon>
          Zurück
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="mobileStepper < 5"
          color="primary"
          depressed
          @click="handleNextStep()"
          :disabled="(mobileStepper === 4 && isButtonDisabled) || (mobileStepper === 2 && hasWarmup === null)">
          Weiter
          <v-icon right>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn v-if="mobileStepper === 5" class="btn-primary-glow" x-large :disabled="isButtonDisabled" @click="onButtonClick">
          <v-icon left>mdi-check-circle-outline</v-icon>
          speichern
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { intensityTechniqueMapping } from "./v2/utils/intensityTechniques"

export default {
  name: "WorkoutSplitEditExerciseSingle",

  props: {
    exerciseProp: { type: Object },
  },

  data() {
    return {
      intensityTechniqueMapping: intensityTechniqueMapping,
      mobileStepper: 1,
      exercise: {
        method: "",
        warmup: null,
        sets: [],
        set_exercise_settings: [],
        general_exercise_settings: [],
      },
      savedGeneralExerciseSettings: false,
      editingSetIndex: null,
      selectedTechniqueMode: "normal",
      isIntensityAlertExpanded: false,
      techniqueModes: [],
      allowArbitraryWeight: false,
      isIntensityAlertExpandedMobile: false,
      techniqueDropset: { maxWeight: 0, setCount: 3, minReps: 8, maxReps: 12, reductionWeight: 5 },
      techniqueReverseDropset: { maxWeight: 0, setCount: 3, minReps: 8, maxReps: 12, expandWeight: 20 },
      techniqueSchockwelle: { startWeight: 0, incrementWeight: 5, pause: 30, setCount: 5, reps: 4 },
      techniqueFst7: { weight: 0, pause: 30, setCount: 7, reps: 7 },
      // NEU: Angepasstes Datenobjekt für eure SST-7 Methode
      sstWizardStep: 1,
      techniqueSst7: {
        weightStep1: 0,
        weightStep2: 0,
        weightStep3: 0,
        weightStep4: 0,
      },
      hasWarmup: null,
    }
  },
  computed: {
    exerciseSummary() {
      if (!this.exercise || !this.exercise.sets || this.exercise.sets.length === 0) {
        return { weightRange: "N/A", repRange: "N/A", durationRange: "N/A" }
      }

      const sets = this.exercise.sets
      let minWeight, maxWeight, minRep, maxRep, minDuration, maxDuration

      const weights = sets.map((s) => parseFloat(s.find((i) => i.text === "Gewicht")?.value)).filter((v) => !isNaN(v))
      const minReps = sets.map((s) => parseInt(s.find((i) => i.text === "Min Wiederholungen")?.value)).filter((v) => !isNaN(v))
      const maxReps = sets.map((s) => parseInt(s.find((i) => i.text === "Max Wiederholungen")?.value)).filter((v) => !isNaN(v))
      const durations = sets.map((s) => parseInt(s.find((i) => i.text === "Dauer")?.value)).filter((v) => !isNaN(v))

      minWeight = weights.length ? Math.min(...weights) : null
      maxWeight = weights.length ? Math.max(...weights) : null
      minRep = minReps.length ? Math.min(...minReps) : maxReps.length ? Math.min(...maxReps) : null
      maxRep = maxReps.length ? Math.max(...maxReps) : null
      minDuration = durations.length ? Math.min(...durations) : null
      maxDuration = durations.length ? Math.max(...durations) : null

      let summary = {
        weightRange: minWeight !== null ? (minWeight === maxWeight ? `${minWeight}kg` : `${minWeight} - ${maxWeight}kg`) : "N/A",
        repRange: minRep !== null ? (minRep === maxRep ? `${minRep} Wdh.` : `${minRep} - ${maxRep} Wdh.`) : "N/A",
        durationRange: minDuration !== null ? (minDuration === maxDuration ? `${minDuration}s` : `${minDuration} - ${maxDuration}s`) : "N/A",
      }
      return summary
    },
    orderedSetSettings() {
      if (!this.exercise || !this.exercise.set_exercise_settings) return []
      const order = ["Gewicht", "Dauer", "Min Wiederholungen", "Max Wiederholungen", "Pause", "Notiz"]
      const settings = [...this.exercise.set_exercise_settings]

      return settings.sort((a, b) => {
        const aIndex = order.indexOf(a.text)
        const bIndex = order.indexOf(b.text)
        if (aIndex === -1) return 1
        if (bIndex === -1) return -1
        return aIndex - bIndex
      })
    },
    hasUnconfirmedDeviceSettings() {
      if (this.savedGeneralExerciseSettings) {
        return false
      } // Gibt true zurück, wenn mindestens eine Einstellung einen Wert hat.

      return this.exercise.general_exercise_settings.some((setting) => setting.value)
    },

    isSensoProExercise() {
      return this.exercise && this.exercise.title === "Senso Pro"
    },

    isWeightlessExercise() {
      if (!this.exercise || !this.exercise.set_exercise_settings) {
        return false
      }

      return !this.exercise.set_exercise_settings.some((setting) => setting.text === "Gewicht")
    },

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },

    mobileTechniqueModes() {
      return this.techniqueModes.filter((mode) => mode.value !== "warmup")
    },

    availableTechniqueModes() {
      // NEU: "sst7" zur Reihenfolge hinzugefügt
      const modeOrder = ["warmup", "normal", "dropset", "reversedropset", "schockwelle", "fst7", "sst7"]

      let modes = modeOrder

        .map((key) => {
          const tech = this.intensityTechniqueMapping[key]

          if (!tech) return null

          return {
            text: tech.descriptionShort,

            value: key,

            icon: tech.icon,
          }
        })

        .filter(Boolean) // Wenn die Übung kein Gewicht hat, zeige nur Aufwärm- und normale Sätze.

      if (this.isWeightlessExercise) {
        const allowedModes = ["warmup", "normal"]

        return modes.filter((mode) => allowedModes.includes(mode.value))
      }

      return modes
    },

    combinedExerciseSets() {
      if (!this.exercise) return []

      let combined = this.exercise.sets ? JSON.parse(JSON.stringify(this.exercise.sets)) : []

      if (this.exercise.warmup) {
        combined.unshift(this.exercise.warmup)
      }

      return combined
    },

    activeTechniqueInfo() {
      const mode = this.exercise.method || this.selectedTechniqueMode

      if (mode && this.intensityTechniqueMapping[mode]) {
        return this.intensityTechniqueMapping[mode]
      }

      return null
    },

    isButtonDisabled() {
      if (this.isSensoProExercise) {
        // Deaktiviert, wenn KEINE einzige Geräteeinstellung einen Wert hat.

        return !this.exercise.general_exercise_settings.some((s) => s.value)
      } // Bisherige Logik für alle anderen Übungen

      return !this.exercise.sets?.length && !this.exercise.warmup
    },

    tooltipMessage() {
      if (this.isSensoProExercise) {
        return this.isButtonDisabled ? "Mindestens eine Geräteeinstellung eingeben" : "Einstellungen speichern"
      }

      const messages = []

      if (!this.exercise.sets?.length) {
        messages.push("Mindestens einen Satz hinzufügen")
      }

      return messages.join(" ")
    },
  },

  watch: {
    selectedTechniqueMode(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.isIntensityAlertExpanded = false
        if (this.exercise.sets.length > 0) {
          this.exercise.sets = []
          this.exercise.method = ""
        }
      }

      if (!this.isMobile) {
        const index = this.techniqueModes.findIndex((mode) => mode.value === newValue)

        this.updateHighlight(index)
      }
    },
  },

  created() {
    // NEU: "sst7" zur Reihenfolge hinzugefügt
    const modeOrder = ["warmup", "normal", "dropset", "reversedropset", "schockwelle", "fst7", "sst7"]
    this.techniqueModes = modeOrder
      .map((key) => {
        const tech = this.intensityTechniqueMapping[key]
        if (!tech) return null
        return { text: tech.descriptionShort, value: key, icon: tech.icon }
      })
      .filter(Boolean)

    this.exercise = JSON.parse(JSON.stringify(this.exerciseProp))
    if (!this.exercise.warmup) this.$set(this.exercise, "warmup", null)
    if (!this.exercise.sets) this.$set(this.exercise, "sets", [])
    if (!this.exercise.set_exercise_settings)
      this.$set(this.exercise, "set_exercise_settings", [
        { text: "Gewicht", input: "number" },
        { text: "Min Wiederholungen", input: "number" },
        { text: "Max Wiederholungen", input: "number" },
        { text: "Pause", input: "number" },
        { text: "Notiz", input: "text" },
      ])
    if (!this.exercise.general_exercise_settings) this.$set(this.exercise, "general_exercise_settings", [])

    if ((!this.exercise.sets || this.exercise.sets.length === 0) && !this.exercise.warmup) {
      const cachedMinReps = localStorage.getItem("cachedMinReps")
      const cachedMaxReps = localStorage.getItem("cachedMaxReps")
      const cachedPause = localStorage.getItem("cachedPause")

      this.exercise.set_exercise_settings.forEach((setting) => {
        if (setting.text === "Min Wiederholungen" && cachedMinReps) setting.value = cachedMinReps
        if (setting.text === "Max Wiederholungen" && cachedMaxReps) setting.value = cachedMaxReps
        if (setting.text === "Pause" && cachedPause) setting.value = cachedPause
      })
    }

    if (this.isMobile && (!this.exercise.general_exercise_settings || this.exercise.general_exercise_settings.length === 0)) {
      this.mobileStepper = 2
    }

    if (this.isMobile) {
      if (this.exercise.warmup) {
        this.hasWarmup = true
      }
    }
  },

  mounted() {
    if (!this.isMobile) {
      this.$nextTick(() => {
        const initialIndex = this.techniqueModes.findIndex((m) => m.value === this.selectedTechniqueMode)

        this.updateHighlight(initialIndex !== -1 ? initialIndex : 1)
      })
    }
  },

  methods: {
    getSetKey(index, set) {
      const weight = set.find((s) => s.text === "Gewicht")?.value || "w0"
      const reps = set.find((s) => s.text === "Max Wiederholungen")?.value || "r0"
      return `${index}-${weight}-${reps}`
    },
    addWarmupSet() {
      this.hasWarmup = true
      if (!this.exercise.warmup || this.exercise.warmup.length === 0) {
        // Erstellt ein Standard-Warmup-Objekt, damit die Eingabefelder erscheinen
        this.$set(this.exercise, "warmup", [
          { text: "Gewicht", value: "", input: "number" },
          { text: "Min Wiederholungen", value: "", input: "number" },
          { text: "Max Wiederholungen", value: "", input: "number" },
          { text: "Pause", value: "30", input: "number" },
        ])
      }
    },
    handleNextStep() {
      if (this.mobileStepper === 2) {
        // Logic when leaving the warmup step
        if (this.hasWarmup === false) {
          this.$set(this.exercise, "warmup", null)
        } else if (this.hasWarmup === true && !this.exercise.warmup) {
          // If user said yes but there's no data yet, create a default
          this.$set(this.exercise, "warmup", [
            { text: "Gewicht", value: "", input: "number" },
            { text: "Min Wiederholungen", value: "", input: "number" },
            { text: "Max Wiederholungen", value: "", input: "number" },
          ])
        }
      }
      this.mobileStepper++
    },
    resetTechnique() {
      if (confirm("Möchtest du die generierten Sätze wirklich löschen und eine neue Technik auswählen?")) {
        this.exercise.sets = []
        this.exercise.method = ""
        this.selectedTechniqueMode = "normal"
        this.sstWizardStep = 1 // Wizard zurücksetzen
        // Gewichte im Wizard-Formular zurücksetzen
        this.techniqueSst7 = { weightStep1: 0, weightStep2: 0, weightStep3: 0, weightStep4: 0 }
      }
    },
    generateSstSets(step) {
      let newSet = {}
      switch (step) {
        case 1: // Sätze 1-4
          const reps = [12, 6, 4, 2]
          reps.forEach((repCount, index) => {
            newSet = this.createSet({
              weight: this.techniqueSst7.weightStep1,
              reps: repCount,
              pause: 30,
              note: index === 0 ? "Maximalgewicht für 12 Wdh" : "",
            })
            this.exercise.sets.push(newSet)
          })
          this.sstWizardStep = 2 // Gehe zum nächsten Schritt
          break
        case 2: // Satz 5
          newSet = this.createSet({
            weight: this.techniqueSst7.weightStep2,
            reps: { min: 6, max: 9 },
            pause: 30,
            note: "Belastung langsam, zügig zurück",
          })
          this.exercise.sets.push(newSet)
          this.sstWizardStep = 3
          break
        case 3: // Satz 6
          newSet = this.createSet({
            weight: this.techniqueSst7.weightStep3,
            reps: { min: 6, max: 9 },
            pause: 30,
            note: "Belastung zügig, langsam zurück",
          })
          this.exercise.sets.push(newSet)
          this.sstWizardStep = 4
          break
        case 4: // Satz 7
          newSet = this.createSet({
            weight: this.techniqueSst7.weightStep4,
            duration: 30,
            reps: { min: 1 },
            pause: 60, // Längere Pause am Ende
            note: "30s in maximaler Kontraktion der Muskulatur halten",
          })
          this.exercise.sets.push(newSet)
          this.exercise.method = "sst7" // Technik als abgeschlossen markieren
          break
      }
    },
    createSet(config) {
      const newSetTemplate = JSON.parse(JSON.stringify(this.exercise.set_exercise_settings))

      newSetTemplate.forEach((setting) => {
        setting.value = "" // Erstmal alles zurücksetzen
        switch (setting.text) {
          case "Gewicht":
            setting.value = config.weight
            break
          case "Pause":
            setting.value = config.pause
            break
          case "Notiz":
            setting.value = config.note
            break
          case "Min Wiederholungen":
            if (config.reps?.min) setting.value = config.reps.min
            break
          case "Max Wiederholungen":
            if (config.reps?.max) setting.value = config.reps.max
            else if (typeof config.reps === "number") setting.value = config.reps
            break
          case "Dauer":
            if (config.duration) setting.value = config.duration
            break
        }
      })
      return newSetTemplate
    },
    shortenLabel(text) {
      return text.replace("Wiederholungen", "WH")
    },
    toggleIntensityAlertExpansionMobile(val) {
      if (this.isIntensityAlertExpandedMobile === val) return (this.isIntensityAlertExpandedMobile = false)

      this.isIntensityAlertExpandedMobile = val
    },

    toggleIntensityAlertExpansion() {
      this.isIntensityAlertExpanded = !this.isIntensityAlertExpanded
    },

    getSectionHeadlineColor(colorName, isSecondary = false) {
      if (this.$vuetify.theme.themes.dark[colorName]) {
        return this.$vuetify.theme.themes.dark[colorName]
      }

      return isSecondary ? "#42A5F5" : "#66BB6A" // Fallback: Blau / Grün
    },

    getIconColorForItem(type) {
      if (type === "advantage") return "green lighten-1"

      if (type === "reason") return "blue lighten-1"

      return "grey"
    },

    formatSetForChip(set) {
      const weight = set.find((s) => s.text === "Gewicht")?.value
      const duration = set.find((s) => s.text === "Dauer")?.value
      const minReps = set.find((s) => s.text === "Min Wiederholungen")?.value
      const maxReps = set.find((s) => s.text === "Max Wiederholungen")?.value
      const pause = set.find((s) => s.text === "Pause")?.value
      console.log
      let output = ""

      if (weight) output += `${weight}kg`

      let repsPart = ""
      if (duration && this.selectedTechniqueMode === "sst7") {
        repsPart = `${duration} s halten`
      } else if (duration) {
        repsPart = `${duration} minuten`
      } else if (minReps && maxReps) {
        repsPart = `${minReps}-${maxReps} Wdh.`
      } else if (minReps) {
        repsPart = `${minReps} Wdh.`
      } else if (maxReps) {
        repsPart = minReps ? `bis ${maxReps} Wdh.` : `${maxReps} Wdh.`
      }

      if (output && repsPart) output += " / "
      if (repsPart && pause) {
        repsPart += ` (${pause}s Pause)`
      } else if (pause) {
        repsPart = `${repsPart} (${pause}s Pause)`
      }
      output += repsPart

      return output.trim()
    },

    onButtonClick() {
      if (this.isButtonDisabled) return

      if (!this.isMobile && !this.isSensoProExercise && this.exercise.general_exercise_settings.length > 0 && this.hasUnconfirmedDeviceSettings) {
        const userConfirmed = confirm(
          "Du hast die Geräteeinstellungen noch nicht mit dem Haken bestätigt. Sollen die eingegebenen Werte trotzdem gespeichert werden?\n\nKlicke auf 'Abbrechen', um zurückzugehen und den Haken zu setzen."
        )

        if (!userConfirmed) {
          return
        }
      }

      if (this.isSensoProExercise) {
        this.exercise.isConfigured = true
      } else {
        let weights = this.exercise.sets.map((set) => parseFloat(set.find((item) => item.text === "Gewicht")?.value || 0))

        let minReps = this.exercise.sets.map((set) => parseInt(set.find((item) => item.text === "Min Wiederholungen")?.value || 0))

        let maxReps = this.exercise.sets.map((set) => parseInt(set.find((item) => item.text === "Max Wiederholungen")?.value || 0))

        this.exercise.weightSum = { min: Math.min(...weights), max: Math.max(...weights) }

        this.exercise.repSum = { min: Math.min(...minReps), max: Math.max(...maxReps) }
      }

      this.$emit("savedExercise", this.exercise)
    },
    getSetTitle(index) {
      if (this.exercise.warmup && index === 0) return "Aufwärmsatz"

      const setIndexInWorkout = this.exercise.warmup ? index : index + 1

      if (this.exercise.method === "sst7") {
        return `SST-7 Satz ${setIndexInWorkout}`
      }

      return `Satz ${setIndexInWorkout}`
    },
    getSetIcon(index) {
      if (this.exercise.warmup && index === 0) return { icon: "mdi-fire", color: "orange" }

      if (this.exercise.method === "sst7") {
        const setIndex = this.exercise.warmup ? index - 1 : index
        if (setIndex < 4) return { icon: "mdi-numeric-" + (setIndex + 1) + "-box-outline", color: "primary" }
        if (setIndex === 4) return { icon: "mdi-arrow-up-down", color: "yellow darken-2" }
        if (setIndex === 5) return { icon: "mdi-arrow-down-up", color: "yellow darken-2" }
        if (setIndex === 6) return { icon: "mdi-timer-sand", color: "red darken-1" }
      }

      return { icon: "mdi-weight-lifter", color: "primary" }
    },
    saveSet() {
      if (this.exercise.set_exercise_settings.find((setting) => setting.value)) {
        if (this.exercise.set_exercise_settings.find((setting) => setting.text === "Gewicht" && setting.value <= 0)) {
          alert("Das Gewicht muss größer als 0 sein.")
          return
        }

        const newSet = JSON.parse(JSON.stringify(this.exercise.set_exercise_settings))

        const minReps = newSet.find((s) => s.text === "Min Wiederholungen")?.value
        const maxReps = newSet.find((s) => s.text === "Max Wiederholungen")?.value
        const pause = newSet.find((s) => s.text === "Pause")?.value
        if (minReps) localStorage.setItem("cachedMinReps", minReps)
        if (maxReps) localStorage.setItem("cachedMaxReps", maxReps)
        if (pause) localStorage.setItem("cachedPause", pause)

        if (this.selectedTechniqueMode === "warmup") {
          this.$set(this.exercise, "warmup", newSet)
          this.selectedTechniqueMode = "normal"
          return
        }

        if (this.editingSetIndex !== null) {
          // *** START KORREKTUR ***
          // Prüfen, ob der Aufwärmsatz bearbeitet wird
          if (this.exercise.warmup && this.editingSetIndex === 0) {
            this.$set(this.exercise, "warmup", newSet)
          } else {
            // Bisherige Logik für normale Arbeitssätze
            const indexToUpdate = this.exercise.warmup ? this.editingSetIndex - 1 : this.editingSetIndex
            this.exercise.sets.splice(indexToUpdate, 1, newSet)
          }
          // *** ENDE KORREKTUR ***

          this.cancelEditSet() // Ruft jetzt die zurückgesetzte Version auf
        } else {
          this.exercise.sets.push(newSet)
        }
      } else {
        alert("Füge mindestens eine Satzeinstellung hinzu")
      }
    },
    removeSetByIndex(index) {
      // *** START KORREKTUR ***
      // Die Bedingung wird erweitert: Der Aufwärmsatz wird gelöscht, wenn der Index -1 ist (mobile Ansicht)
      // ODER wenn der Index 0 ist UND ein Aufwärmsatz existiert (Desktop-Ansicht).
      if (index === -1 || (this.exercise.warmup && index === 0)) {
        this.removeWarmupSet()
        this.cancelEditSet() // Form zurücksetzen
        return
      }

      // Korrekte Index-Berechnung für das `sets`-Array
      const actualIndex = this.exercise.warmup ? index - 1 : index
      this.exercise.sets.splice(actualIndex, 1)
      // *** ENDE KORREKTUR ***

      this.cancelEditSet() // Form zurücksetzen

      // Optional: Form mit den Werten des letzten Satzes füllen
      if (this.exercise.sets.length > 0) {
        this.exercise.set_exercise_settings = JSON.parse(JSON.stringify(this.exercise.sets[this.exercise.sets.length - 1]))
      }
    },

    removeWarmupSet() {
      this.exercise.warmup = null
      this.hasWarmup = null
    },

    editSet(index) {
      if (this.exercise.method || this.selectedTechniqueMode === "sst7") {
        return
      }
      if (this.exercise.warmup && index === 0) {
        this.editingSetIndex = 0

        this.exercise.set_exercise_settings = JSON.parse(JSON.stringify(this.exercise.warmup))

        return
      }

      const actualIndex = this.exercise.warmup ? index - 1 : index

      this.editingSetIndex = index

      this.exercise.set_exercise_settings = JSON.parse(JSON.stringify(this.exercise.sets[actualIndex]))
    },

    cancelEditSet() {
      this.editingSetIndex = null
      if (this.exercise.sets.length > 0) {
        // Fülle die Form mit den Werten des letzten Satzes
        this.exercise.set_exercise_settings = JSON.parse(JSON.stringify(this.exercise.sets[this.exercise.sets.length - 1]))
      } else {
        // Wenn keine Sätze mehr da sind, Formularfelder leeren
        this.exercise.set_exercise_settings.forEach((setting) => {
          setting.value = ""
        })
      }
    },

    openCancelConfirm() {
      if (this.hasUnconfirmedDeviceSettings) {
        if (confirm("Du hast Geräteeinstellungen eingegeben, aber nicht mit dem Haken bestätigt. Trotzdem abbrechen?")) {
          this.$emit("cancel-edit")
        }

        return // Wichtig, damit die restliche Funktion nicht ausgeführt wird.
      }

      if (this.exercise.sets.length > 0) {
        if (confirm("Ungespeicherte Änderungen gehen verloren. Wirklich abbrechen?")) {
          this.$emit("cancel-edit")
        }
      } else {
        this.$emit("cancel-edit")
      }
    },

    saveGeneralExerciseSettings() {
      if (!this.exercise.general_exercise_settings.find((setting) => setting.value)) {
        if (confirm("Ohne Geräteeinstellungen fortfahren?")) {
          this.savedGeneralExerciseSettings = true
        }
      } else {
        this.savedGeneralExerciseSettings = true
      }
    },

    updatingSetting(event, item) {
      item.value = event.target.innerText.trim()
    },

    getImageForExercise(exercise) {
      if (exercise.picture) return this.getImageUrl(exercise.picture)

      if (exercise.manual && exercise.manual[0]?.picture) return this.getImageUrl(exercise.manual[0].picture)

      return "/img/einmalig/imageUploadPlaceholder.jpg"
    },

    getImageUrl(image) {
      if (!image) return ""

      return `/img/uebungen/${image.replace(/\.[^/.]+$/, "")}.webp`
    },

    shorten(text, length = 25) {
      if (text === "Min Wiederholungen") return "Min WH"

      if (text === "Max Wiederholungen") return "Max WH"

      if (text === "Umdrehungen pro Minute") return "UPM"

      if (text.length > length) text = text.substr(0, length) + "..."

      return text
    },

    getSettingText(setting) {
      let text = setting.value

      if (setting.text === "Gewicht") text = text + " KG"

      return text
    },

    getSettingValue(set, settingText) {
      const setting = set.find((s) => s.text === settingText)

      return setting ? parseFloat(setting.value) || setting.value : null
    },

    validateWeight(value) {
      if (this.allowArbitraryWeight) return true

      return value % 5 === 0 || "Das Gewicht muss ein Vielfaches von 5 sein."
    },

    generateSetsForTechnique() {
      this.exercise.sets = []

      if (this.selectedTechniqueMode === "dropset") {
        if (!this.techniqueDropset.maxWeight) return alert("Bitte Maximalgewicht angeben.")

        if (this.techniqueDropset.maxWeight <= 0) return alert("Bitte ein positives Maximalgewicht angeben.")

        if (this.techniqueDropset.setCount <= 0) return alert("Bitte eine positive Anzahl an Sätzen angeben.")

        if (this.techniqueDropset.setCount * this.techniqueDropset.reductionWeight >= this.techniqueDropset.maxWeight) {
          return alert("Die Reduktion pro Satz muss kleiner sein als das Maximalgewicht.")
        }

        let currentWeight = parseFloat(this.techniqueDropset.maxWeight)

        for (let i = 0; i < this.techniqueDropset.setCount; i++) {
          const newSet = [
            { text: "Gewicht", value: currentWeight, input: "number" },

            { text: "Min Wiederholungen", value: this.techniqueDropset.minReps, input: "number" },

            { text: "Max Wiederholungen", value: this.techniqueDropset.maxReps, input: "number" },

            { text: "Pause", value: 0, input: "number" },
          ]

          this.exercise.sets.push(newSet)

          currentWeight -= this.techniqueDropset.reductionWeight

          if (!this.allowArbitraryWeight) currentWeight = Math.round(currentWeight / 2.5) * 2.5
        }
      } else if (this.selectedTechniqueMode === "reversedropset") {
        if (!this.techniqueReverseDropset.maxWeight) return alert("Bitte Maximalgewicht angeben.")

        if (this.techniqueReverseDropset.maxWeight <= 0) return alert("Bitte ein positives Maximalgewicht angeben.")

        if (this.techniqueReverseDropset.setCount <= 0) return alert("Bitte eine positive Anzahl an Sätzen angeben.")

        if (this.techniqueReverseDropset.setCount * this.techniqueReverseDropset.expandWeight >= this.techniqueReverseDropset.maxWeight) {
          return alert("Die Gewichtserhöhung pro Satz muss kleiner sein als das Maximalgewicht.")
        }

        let startWeight =
          this.techniqueReverseDropset.maxWeight - (this.techniqueReverseDropset.setCount - 1) * this.techniqueReverseDropset.expandWeight

        let currentWeight = startWeight

        for (let i = 0; i < this.techniqueReverseDropset.setCount; i++) {
          const newSet = [
            { text: "Gewicht", value: Math.round(currentWeight / 2.5) * 2.5, input: "number" },

            { text: "Min Wiederholungen", value: this.techniqueReverseDropset.minReps, input: "number" },

            { text: "Max Wiederholungen", value: this.techniqueReverseDropset.maxReps, input: "number" },

            { text: "Pause", value: 0, input: "number" },
          ]

          this.exercise.sets.push(newSet)

          currentWeight = currentWeight + this.techniqueReverseDropset.expandWeight
        }
      } else if (this.selectedTechniqueMode === "schockwelle") {
        if (!this.techniqueSchockwelle.startWeight) return alert("Bitte Startgewicht angeben.")

        if (this.techniqueSchockwelle.startWeight <= 0) return alert("Bitte ein positives Startgewicht angeben.")

        if (this.techniqueSchockwelle.setCount <= 0) return alert("Bitte eine positive Anzahl an Sätzen angeben.")

        if (this.techniqueSchockwelle.incrementWeight <= 0) return alert("Bitte eine positive Gewichtserhöhung angeben.")

        let currentWeight = parseFloat(this.techniqueSchockwelle.startWeight)

        for (let i = 0; i < this.techniqueSchockwelle.setCount; i++) {
          const newSet = [
            { text: "Gewicht", value: Math.round(currentWeight / 2.5) * 2.5, input: "number" },

            { text: "Max Wiederholungen", value: this.techniqueSchockwelle.reps, input: "number" },

            { text: "Pause", value: this.techniqueSchockwelle.pause, input: "number" },
          ]

          this.exercise.sets.push(newSet)

          currentWeight = currentWeight + this.techniqueSchockwelle.incrementWeight
        }
      } else if (this.selectedTechniqueMode === "fst7") {
        if (!this.techniqueFst7.weight) return alert("Bitte Gewicht angeben.")

        if (this.techniqueFst7.weight <= 0) return alert("Bitte ein positives Gewicht angeben.")

        for (let i = 0; i < this.techniqueFst7.setCount; i++) {
          const newSet = [
            { text: "Gewicht", value: parseFloat(this.techniqueFst7.weight), input: "number" },

            { text: "Max Wiederholungen", value: this.techniqueFst7.reps, input: "number" },

            { text: "Pause", value: this.techniqueFst7.pause, input: "number" },
          ]

          this.exercise.sets.push(newSet)
        }
      }
      // NEU: Komplett überarbeitete Logik für eure SST-7 Methode
      else if (this.selectedTechniqueMode === "sst7") {
        if (!this.techniqueSst7.startWeight || this.techniqueSst7.startWeight <= 0) {
          return alert("Bitte ein positives Startgewicht angeben.")
        }

        const startWeight = parseFloat(this.techniqueSst7.startWeight)
        const reduction = parseFloat(this.techniqueSst7.reductionWeight)

        const setDefinitions = [
          // Sätze 1-4
          { reps: 12, weight: startWeight, note: "Maximalgewicht für 12 Wdh" },
          { reps: 6, weight: startWeight, note: "" },
          { reps: 4, weight: startWeight, note: "" },
          { reps: 2, weight: startWeight, note: "" },
          // Sätze 5-7
          {
            reps: { min: this.techniqueSst7.minReps, max: this.techniqueSst7.maxReps },
            weight: startWeight - reduction,
            note: "Langsam hin, schnell zurück",
          },
          {
            reps: { min: this.techniqueSst7.minReps, max: this.techniqueSst7.maxReps },
            weight: startWeight - 2 * reduction,
            note: "Schnell hin, langsam zurück",
          },
          { duration: 30, weight: startWeight - 3 * reduction, note: "30s statisch halten" },
        ]

        setDefinitions.forEach((def, index) => {
          const newSet = JSON.parse(JSON.stringify(this.exercise.set_exercise_settings))
          let finalWeight = Math.round(def.weight / 2.5) * 2.5
          if (finalWeight < 2.5) finalWeight = 2.5

          newSet.forEach((setting) => {
            setting.value = "" // Erstmal alles zurücksetzen
            switch (setting.text) {
              case "Gewicht":
                setting.value = finalWeight
                break
              case "Pause":
                setting.value = index === setDefinitions.length - 1 ? 60 : 30 // Letzte Pause 60s
                break
              case "Notiz":
                setting.value = def.note
                break
              case "Min Wiederholungen":
                if (def.reps?.min) setting.value = def.reps.min
                break
              case "Max Wiederholungen":
                if (def.reps?.max) setting.value = def.reps.max
                else if (def.reps && !def.reps.min) setting.value = def.reps
                break
              case "Dauer":
                if (def.duration) setting.value = def.duration
                break
            }
          })
          this.exercise.sets.push(newSet)
        })
      }

      this.$set(this.exercise, "method", this.selectedTechniqueMode)
    },

    updateHighlight(index) {
      if (this.isMobile || index < 0) return

      this.$nextTick(() => {
        const btns = this.$el.querySelectorAll(".technique-custom-card")

        const highlight = this.$refs.highlight

        if (!btns.length || !highlight) return

        const selectedBtn = btns[index]

        if (!selectedBtn) return

        highlight.style.width = Number(selectedBtn.offsetWidth) + 4 + "px"

        highlight.style.height = Number(selectedBtn.offsetHeight) + 4 + "px"

        highlight.style.left = selectedBtn.parentElement.offsetLeft + "px"

        highlight.style.top = selectedBtn.parentElement.offsetTop + "px"
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.confirmation-card-wow {
  min-height: 374px;
  position: relative;
  padding: 32px;
  border-radius: 24px;
  background: rgba(30, 30, 30, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
  overflow: hidden; /* Wichtig für den Glow-Effekt */
  animation: card-enter 0.6s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
}

/* Der pulsierende Lichtschein im Hintergrund */
.confirmation-card-wow::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 250px;
  height: 250px;
  background: radial-gradient(circle, var(--glow-color) 0%, rgba(0, 0, 0, 0) 70%);
  transform: translate(-50%, -50%);
  opacity: 0.25;
  filter: blur(50px);
  animation: pulse-glow 6s infinite ease-in-out;
}

/* Wrapper für das Icon, um die Ringe zu positionieren */
.icon-wrapper {
  position: relative;
  display: inline-block;
  z-index: 2; /* Über dem Glow */
  animation: icon-pop-in 0.8s 0.2s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
  transform: scale(0);
}

/* Animierte Ringe, die vom Icon ausgehen */
.icon-wrapper::before,
.icon-wrapper::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 2px solid var(--glow-color);
  opacity: 0;
  animation: radiate 3s cubic-bezier(0.075, 0.82, 0.165, 1);
}

/* Der zweite Ring startet mit einer Verzögerung */
.icon-wrapper::after {
  animation-delay: 1.25s;
}

/* Alle Textelemente bekommen eine gestaffelte Fade-In Animation */
.confirmation-card-wow > * {
  position: relative;
  z-index: 2;
}

.confirmation-card-wow h3,
.confirmation-card-wow p,
.confirmation-card-wow .reset-button {
  opacity: 0;
  animation: fade-in-up 0.5s ease-out forwards;
}

.confirmation-card-wow h3 {
  animation-delay: 0.4s;
}
.confirmation-card-wow p.body-1 {
  animation-delay: 0.5s;
}
.confirmation-card-wow p.caption {
  animation-delay: 0.6s;
}
.confirmation-card-wow .reset-button {
  animation-delay: 0.8s;
}

/* KEYFRAME ANIMATIONEN */

/* Animation für die Karte selbst */
@keyframes card-enter {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Animation für das pulsierende Hintergrundlicht */
@keyframes pulse-glow {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.25;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0.4;
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.25;
  }
}

/* Animation für die "strahlenden" Ringe */
@keyframes radiate {
  from {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.6;
  }
  to {
    transform: translate(-50%, -50%) scale(2.5);
    opacity: 0;
  }
}

/* Animation für das Icon */
@keyframes icon-pop-in {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

/* Animation für den Text */
@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.line-height-tight {
  line-height: 1.2;
}
.v-stepper {
  background-color: transparent !important;
}
.v-stepper__content {
  padding: 16px 0px 16px 24px;
}
::v-deep .v-stepper__step__step {
  margin-right: 12px;
}

/* Stile für Tablet/Desktop (aus vorherigen Versionen) */
.compact-input {
  width: auto;
  min-width: 50px;
  border: none;
  background: transparent;
  padding: 5px;
  font-size: 14px;
  transition: width 0.3s, border-color 0.3s;
  caret-color: white;
}
.compact-input:empty:before {
  content: "+";
  color: white;
}
.compact-input:focus {
  border-color: white; /* visuelles Feedback beim Fokus */
}
.technique-custom-card {
  position: relative;
  background: black;
  overflow: hidden;
  transition: all 0.3s ease;
  border-radius: 8px;
  color: #d7d7d7;
}

.input-card {
  border-radius: 12px;
  transition: box-shadow 0.2s ease-out;
  width: 100px;
}

/* KORRIGIERT: :focus-within als eigene Regel, nicht verschachtelt */
.input-card:focus-within {
  box-shadow: 0 0 10px rgba(var(--v-primary-base-rgb), 0.5);
}

.highlight {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background-color: var(--v-primary); /* Fallback color */
  border-radius: 8px;
  transition: all 0.3s ease;
}

.builder-background {
  width: 100%;
  height: 100%;
  color: #fff;
  overflow-y: auto;
  position: relative;
  background: #1a1a1a;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.gradient-bg {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: radial-gradient(circle at 15% 25%, rgba(108, 99, 255, 0.15), transparent 45%),
    radial-gradient(circle at 85% 75%, rgba(var(--v-primary-base-rgb), 0.2), transparent 45%);
  animation: rotate-gradient 25s linear infinite;
  z-index: 1;
}
@keyframes rotate-gradient {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.content-wrapper {
  position: relative;
  z-index: 2;
  padding: 24px;
}
.glass-card {
  background: rgba(44, 44, 46, 0.6);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}
.technique-group {
  display: flex;
  gap: 12px;
  background: rgba(0, 0, 0, 0.2);
  padding: 8px;
  border-radius: 12px;
}
.technique-item {
  flex: 1;
  flex-direction: column;
  display: flex;
  text-align: center;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  .icon {
    color: #9e9e9e;
    transition: all 0.3s ease;
    margin-right: 8px;
  }
  .label {
    font-size: 0.9rem;
    color: #9e9e9e;
    font-weight: 500;
    transition: all 0.3s ease;
  }
}
.technique-item:hover {
  background: rgba(255, 255, 255, 0.05);
  .icon,
  .label {
    color: white;
  }
}
.technique-item.active {
  background: rgba(var(--v-primary-base-rgb), 0.2);
  border-color: var(--v-primary-base);
  box-shadow: 0 0 20px rgba(var(--v-primary-base-rgb), 0.4);
  .icon,
  .label {
    color: white;
  }
}
.sets-display-desktop {
  display: grid;
  grid-template-columns: 30% 68%;
  gap: 24px;
}
.sets-list-wrapper {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 12px;
}
.set-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  border-left: 3px solid transparent;
  transition: all 0.2s ease;
}
.set-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-left-color: var(--v-primary-base);
}
.set-icon {
  margin-right: 16px;
}
.set-details {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}
.edit-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
}
.set-item:hover .edit-icon {
  opacity: 0.7;
}

::v-deep .input-form-wrapper .v-text-field--solo-inverted.v-input--is-focused .v-input__slot {
  background: rgba(var(--v-primary-base-rgb), 0.2) !important;
}

.main-save-btn {
  box-shadow: 0 0 20px rgba(var(--v-primary-base-rgb), 0.5);
  transition: all 0.3s ease;
}
.main-save-btn:hover:not(:disabled) {
  box-shadow: 0 4px 30px rgba(var(--v-primary-base-rgb), 0.7);
  transform: translateY(-2px);
}

/* Basis-Stil für alle neuen Buttons */
.btn-base {
  border-radius: 12px !important; /* Einheitlicher Radius */
  font-weight: bold !important;
  letter-spacing: 0.05em !important;
  text-transform: none !important; /* Moderner Look ohne Grossbuchstaben */
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: none;
}

/* 1. Primäre Aktionen (Speichern, Hinzufügen, Generieren) */
.btn-primary-glow {
  @extend .btn-base;
  background: linear-gradient(45deg, var(--v-primary), var(--v-primary)) !important;
  color: white !important;
  box-shadow: 0 4px 15px 0 rgba(0, 150, 63, 0.35);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px 0 rgba(0, 150, 63, 0.45);
  }
}

/* 2. Destruktive Aktionen (Löschen, Schliessen) */
.btn-danger-glow {
  @extend .btn-base;
  background: linear-gradient(45deg, #ff7043, #e64a19) !important;
  color: white !important;
  box-shadow: 0 4px 15px 0 rgba(230, 74, 25, 0.35);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px 0 rgba(230, 74, 25, 0.45);
  }
}

/* 3. Sekundäre Aktionen (Abbrechen) */
.btn-secondary {
  @extend .btn-base;
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  color: rgba(255, 255, 255, 0.7) !important;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.15) !important;
    color: white !important;
  }
}

/* 4. Kleine Icon-Buttons (Bleistift, Haken in Formularen) */
.btn-icon-subtle {
  @extend .btn-base;
  background: rgba(255, 255, 255, 0.1) !important;
  border-radius: 8px !important;
  min-width: 36px !important; // Stellt sicher, dass sie quadratisch sind
  width: 36px;
  height: 36px !important;

  .v-icon {
    color: rgba(255, 255, 255, 0.6) !important;
    transition: all 0.2s ease;
  }

  &:hover:not(:disabled) {
    background: rgba(var(--v-primary-base-rgb), 0.2) !important;
    .v-icon {
      color: white !important;
      transform: scale(1.1);
    }
  }
}

/* Disabled-Status für alle Glow-Buttons */
.btn-primary-glow:disabled,
.btn-danger-glow:disabled {
  background: rgba(80, 80, 80, 0.5) !important;
  box-shadow: none !important;
  color: rgba(255, 255, 255, 0.3) !important;
}

// --- Stile für Mobile Wizard (NEU & ANGEPASST) ---
.mobile-wizard-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #121212;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  color: white;
}

.mobile-wizard-progress {
  position: absolute;
  top: 80px; /* Below app-bar */
  left: 0;
  width: 100%;
  z-index: 1;
}

.mobile-wizard-content {
  flex-grow: 1;
  margin-bottom: 64px;
  overflow-y: auto;
  padding: 80px 24px 100px 24px;
}

.mobile-wizard-step {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.mobile-wizard-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(to top, rgba(18, 18, 18, 1) 70%, rgba(18, 18, 18, 0));
  display: flex;
  align-items: center;
  z-index: 2;
}

.choice-card {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1) !important;
    transform: translateY(-2px);
  }
}

.technique-tile {
  border: 2px solid rgba(255, 255, 255, 0.1);
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 16px !important;
  transition: all 0.25s cubic-bezier(0.165, 0.84, 0.44, 1);

  &--active {
    background-color: var(--tech-color);
    border-color: var(--tech-color);
    transform: scale(1.02);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);

    .v-icon,
    .font-weight-bold,
    .caption {
      color: white !important;
    }
  }
}

.generated-sets-list {
  max-height: 250px;
  overflow-y: auto;
  margin-top: 16px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}

// --- Stile aus der originalen Desktop-Version (für Konsistenz) ---
.confirmation-card-wow {
  /* (Identisch zu den Styles aus dem ersten Prompt) */
}
// ... (alle @keyframes, .btn-primary-glow, .glass-card etc. hier einfügen)
.input-card {
  border-radius: 12px;
  transition: box-shadow 0.2s ease-out;
  width: 100%; // Angepasst für mobiles Layout
}
.input-card:focus-within {
  box-shadow: 0 0 10px rgba(var(--v-primary-base-rgb), 0.5);
}
.centered-input ::v-deep input {
  text-align: center;
}
.set-list-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.set-list-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px !important;
  border-left: 4px solid var(--border-color, #424242);
  transition: all 0.3s ease-in-out;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    background-color: rgba(255, 255, 255, 0.08);
  }
}

/* Animationen für die Liste */
.set-list-enter-active,
.set-list-leave-active {
  transition: all 0.4s cubic-bezier(0.55, 0, 0.1, 1);
}
.set-list-enter {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}
.set-list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
